import { useState, useEffect, useRef } from "react";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ChevronDown, CreditCard, Plus, ArrowDown } from "lucide-react";
import ClearingMirror from "@/components/ClearingMirror";
import WhyMickey from "@/components/WhyMickey";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const DEFAULT_ORDER = ["hero", "emotional_intro", "who_this_is_for", "what_this_is", "clearing_mirror", "how_it_works", "workshops_cta", "dialogue", "why_mickey", "what_this_is_not", "packages", "reflection_library", "scholarship", "testimonials", "faq", "final_cta", "media_kit", "footer_note"];

export default function MirrorConversations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingPkg, setBookingPkg] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  const [scholarshipOpen, setScholarshipOpen] = useState(false);
  const contactRef = useRef(null);
  useSEO("mirror-conversations");

  useEffect(() => {
    http.get("/mirror/public/data")
      .then(res => setData(res.data))
      .catch(() => toast.error("Could not load page."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (loading) return (
    <Layout>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#D4AF37] tracking-[0.4em] text-xs uppercase animate-pulse font-sans">Reflecting...</div>
      </div>
    </Layout>
  );
  if (!data) return <Layout><div className="min-h-screen bg-[#0A0A0A]" /></Layout>;

  let order = data.section_order || DEFAULT_ORDER;
  if (!order.includes("workshops_cta")) {
    order = [...order];
    const idx = order.indexOf("how_it_works");
    order.splice(idx >= 0 ? idx + 1 : 6, 0, "workshops_cta");
  }
  const map = {
    hero: <Hero key="hero" d={data} />,
    emotional_intro: <EmotionalIntro key="ei" d={data} />,
    who_this_is_for: <WhoThisIsFor key="who" d={data} />,
    what_this_is: <WhatThisIs key="what" d={data} />,
    clearing_mirror: <ClearingMirror key="cm" d={data} />,
    how_it_works: <HowItWorks key="how" d={data} />,
    workshops_cta: <WorkshopsCTA key="wscta" />,
    dialogue: <Dialogue key="dial" d={data} />,
    why_mickey: <WhyMickey key="why" d={data} />,
    what_this_is_not: <WhatThisIsNot key="not" d={data} />,
    packages: <Packages key="pkg" d={data} onBook={setBookingPkg} contactRef={contactRef} />,
    reflection_library: <ReflectionLibraryCTA key="rl" d={data} />,
    scholarship: <Scholarship key="schol" d={data} onOpen={() => setScholarshipOpen(true)} />,
    testimonials: <Testimonials key="test" d={data} />,
    faq: <Faq key="faq" d={data} />,
    final_cta: <FinalCTA key="final" d={data} />,
    media_kit: <MediaKit key="mk" d={data} contactRef={contactRef} />,
    footer_note: <FooterNote key="fn" d={data} contactRef={contactRef} />,
  };

  const hidden = data.hidden_sections || [];

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-mc-h { font-family: 'Cormorant Garamond', serif; }
        .font-mc-b { font-family: 'DM Sans', sans-serif; }
        .gold-glow { box-shadow: 0 0 20px rgba(212,175,55,0.35); }
        .gold-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent); }
      `}</style>

      {/* Anchor nav - desktop */}
      <nav className="hidden lg:flex fixed top-20 right-6 z-40 flex-col gap-2 text-right" data-testid="anchor-nav">
        {[["how_it_works", "How It Works"], ["why_mickey", "Why Mickey?"], ["packages", "Choose Your Seat"], ["faq", "FAQ"]]
          .filter(([id]) => !hidden.includes(id))
          .map(([id, label]) => (
          <a key={id} href={`#${id}`} className="font-mc-b text-[10px] tracking-[0.15em] uppercase text-zinc-500 hover:text-[#D4AF37] transition-colors">{label}</a>
        ))}
      </nav>

      {order.filter(k => !hidden.includes(k)).map(k => map[k] || null)}

      {/* Sticky mobile CTA */}
      <AnimatePresence>
        {showSticky && (
          <motion.a initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} href="/book"
            className="fixed bottom-6 left-6 right-6 z-50 bg-[#D4AF37] text-[#0A0A0A] text-center py-4 rounded-full font-mc-b font-medium text-sm tracking-wide uppercase shadow-[0_4px_20px_rgba(0,0,0,0.5)] lg:hidden" data-testid="sticky-cta">
            Take a Seat
          </motion.a>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bookingPkg && <BookingModal pkg={bookingPkg} minPrice={data.min_price} pwyw={data.pwyw_enabled} pwywText={data.pwyw_text} suggestedPrice={data.suggested_price} onClose={() => setBookingPkg(null)} />}
      </AnimatePresence>
      <AnimatePresence>
        {scholarshipOpen && <ScholarshipModal d={data} onClose={() => setScholarshipOpen(false)} />}
      </AnimatePresence>
    </Layout>
  );
}

/* ═══════════════════ HERO ═══════════════════ */
function Hero({ d }) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0A0A0A]" data-testid="section-hero">
      <div className="absolute inset-0 z-0">
        <img src={resolveImage(d.hero_image)} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.p variants={fadeUp} className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-8">Mirror Conversations&trade;</motion.p>
          <motion.h1 variants={fadeUp} className="font-mc-h text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.1] text-[#F5F5F0] mb-8 whitespace-pre-line">
            {d.hero_headline}
          </motion.h1>
          <motion.p variants={fadeUp} className="font-mc-b text-base md:text-lg text-zinc-300 leading-relaxed max-w-2xl mx-auto mb-6">{d.hero_subheadline}</motion.p>
          <motion.p variants={fadeUp} className="font-mc-b text-sm text-zinc-500 italic mb-12">{d.hero_trust_line}</motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all duration-300 px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-testid="hero-cta-primary">{d.hero_primary_btn || "Take a Seat"}</Link>
            <a href="#how_it_works" className="border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 transition-all duration-300 px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm" data-testid="hero-cta-secondary">{d.hero_secondary_btn || "See How It Works"}</a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-4 flex justify-center">
            <Link to="/workshops" className="border border-[#D4AF37]/40 text-[#D4AF37]/80 hover:border-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] transition-all duration-300 px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm" data-testid="hero-cta-workshops">Available Workshops</Link>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <ArrowDown size={20} className="text-zinc-600 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ EMOTIONAL INTRO ═══════════════════ */
function EmotionalIntro({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-emotional-intro">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="max-w-3xl mx-auto text-center">
        <div className="font-mc-h text-xl md:text-2xl text-[#F5F5F0] font-light leading-[1.8] whitespace-pre-line">
          {d.emotional_intro}
        </div>
      </motion.div>
    </section>
  );
}

/* ═══════════════════ WHO THIS IS FOR ═══════════════════ */
function WhoThisIsFor({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-who-this-is-for">
      <div className="max-w-6xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-20">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Who this is for</p>
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0]">{d.who_heading}</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {d.who_cards?.map((card, i) => (
            <motion.div key={i} variants={fadeUp} className="rounded-2xl p-8 bg-white/[0.03] backdrop-blur-md border border-white/[0.06] hover:border-[#D4AF37]/40 transition-all duration-300 group" data-testid={`who-card-${i}`}>
              <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:border-[#D4AF37] transition-colors">
                <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
              </div>
              <p className="font-mc-h text-xl text-[#F5F5F0] font-light leading-relaxed">{card.text}</p>
            </motion.div>
          ))}
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-16">
          <p className="font-mc-h text-lg text-zinc-400 italic whitespace-pre-line mb-10">{d.who_subtext}</p>
          <Link to="/book" className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all duration-300 px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-testid="who-cta">{d.who_btn_text}</Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ WHAT THIS IS ═══════════════════ */
function WhatThisIs({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-what-this-is">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="order-2 lg:order-1">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">The mirror method</p>
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-8">{d.what_heading}</h2>
          <div className="font-mc-b text-base text-zinc-300 leading-[1.9] whitespace-pre-line mb-10">{d.what_copy}</div>
          <div className="border-l-2 border-[#D4AF37]/40 pl-6">
            <p className="font-mc-h text-2xl md:text-3xl text-[#F5F5F0] font-light italic">&ldquo;{d.what_pullquote}&rdquo;</p>
          </div>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="order-1 lg:order-2">
          <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
            <img src={resolveImage(d.what_image)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ HOW IT WORKS ═══════════════════ */
function HowItWorks({ d }) {
  return (
    <section id="how_it_works" className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-how-it-works">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-20">
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0]">{d.how_heading}</h2>
        </motion.div>
        <div className="grid md:grid-cols-4 gap-0">
          {d.how_steps?.map((step, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: i * 0.15 }} className="relative text-center px-6 py-8" data-testid={`how-step-${i}`}>
              <div className="text-[#D4AF37] font-mc-b text-xs tracking-[0.3em] uppercase mb-4">Step {i + 1}</div>
              <div className="w-12 h-12 mx-auto rounded-full border border-[#D4AF37]/40 flex items-center justify-center mb-6">
                <span className="font-mc-h text-2xl text-[#D4AF37] font-light">{i + 1}</span>
              </div>
              <h3 className="font-mc-h text-xl text-[#F5F5F0] mb-3">{step.title}</h3>
              <p className="font-mc-b text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
              {i < (d.how_steps?.length || 0) - 1 && <div className="hidden md:block absolute top-1/2 right-0 w-full h-px gold-divider -translate-y-4" />}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ WORKSHOPS CTA ═══════════════════ */
function WorkshopsCTA() {
  const [workshops, setWorkshops] = useState([]);
  useEffect(() => {
    http.get("/workshops/public/workshops").then(r => setWorkshops(r.data.filter(w => w.seats_remaining > 0).slice(0, 2))).catch(() => {});
  }, []);

  return (
    <section className="relative py-24 md:py-32 px-6 overflow-hidden" data-testid="section-workshops-cta">
      {/* Warm amber gradient background — visually distinct from the dark sections */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A150D] via-[#16120A] to-[#0A0A0A]" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(212,175,55,0.08) 0%, transparent 100%)" }} />
      <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[1px] h-16 bg-gradient-to-b from-transparent via-[#D4AF37]/40 to-transparent" />

      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center mb-14">
          <motion.p variants={fadeUp} className="font-mc-b text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5">Live Experience</motion.p>
          <motion.h2 variants={fadeUp} className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] leading-tight mb-5">The Mirror Comes to Life</motion.h2>
          <motion.p variants={fadeUp} className="font-mc-b text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            Step beyond the screen. Our in-person workshops bring the Mirror Conversation experience into a shared room &mdash; where real people sit across from one another and practice the kind of honesty that changes everything.
          </motion.p>
        </motion.div>

        {workshops.length > 0 && (
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-5 mb-12 max-w-3xl mx-auto">
            {workshops.map(w => (
              <motion.div key={w.id} variants={fadeUp}>
                <Link to={`/workshops/${w.slug}`} className="block rounded-2xl border border-[#D4AF37]/20 bg-white/[0.03] backdrop-blur-sm p-7 hover:border-[#D4AF37]/50 hover:bg-white/[0.05] transition-all duration-300 group" data-testid={`ws-cta-card-${w.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
                    <span className="font-mc-b text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Upcoming</span>
                  </div>
                  <h3 className="font-mc-h text-xl text-[#F5F5F0] font-light mb-2 group-hover:text-[#D4AF37] transition-colors">{w.title}</h3>
                  <div className="font-mc-b text-sm text-zinc-500 space-y-1">
                    <p>{new Date(w.date + "T00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} &middot; {w.start_time}</p>
                    <p>{w.location_name}</p>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
                    <span className="font-mc-h text-lg text-[#F5F5F0] font-light">${(w.price / 100).toFixed(0)} <span className="text-zinc-600 text-sm">/ seat</span></span>
                    <span className={`font-mc-b text-[10px] tracking-wider uppercase ${w.seats_remaining <= 3 ? "text-amber-400" : "text-zinc-500"}`}>
                      {w.seats_remaining <= 3 ? `Only ${w.seats_remaining} left` : `${w.seats_remaining} seats open`}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center">
          <Link to="/workshops" className="inline-block bg-[#D4AF37] text-[#0A0A0A] px-12 py-5 rounded-full font-mc-b tracking-wide uppercase text-sm gold-glow hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300" data-testid="ws-cta-browse">
            Browse All Workshops
          </Link>
          <p className="font-mc-b text-xs text-zinc-600 mt-6 italic">Limited capacity. Every seat matters.</p>
        </motion.div>
      </div>

      <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[1px] h-16 bg-gradient-to-b from-transparent via-[#D4AF37]/40 to-transparent" />
    </section>
  );
}

/* ═══════════════════ DIALOGUE ═══════════════════ */
function Dialogue({ d }) {
  return (
    <section className="bg-[#0D0D0D] py-28 md:py-36 px-6" data-testid="section-dialogue">
      <div className="max-w-3xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0]">{d.dialogue_heading}</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-5">
          {d.dialogue_lines?.map((line, i) => (
            <motion.div key={i} variants={fadeUp} className={`flex ${line.speaker === "mickey" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-6 py-4 ${line.speaker === "mickey"
                ? "bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-bl-sm"
                : "bg-white/[0.05] border border-white/[0.08] rounded-br-sm"}`}>
                <p className="font-mc-b text-[10px] tracking-[0.2em] uppercase mb-1.5 text-zinc-500">{line.speaker === "mickey" ? "Mickey" : "Person"}</p>
                <p className="font-mc-h text-lg text-[#F5F5F0] font-light italic">&ldquo;{line.text}&rdquo;</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-16">
          <div className="gold-divider mb-12" />
          <p className="font-mc-h text-xl text-zinc-400 font-light italic whitespace-pre-line leading-relaxed">{d.dialogue_closing}</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ WHAT THIS IS NOT ═══════════════════ */
function WhatThisIsNot({ d }) {
  return (
    <section className="bg-[#0D0D0D] py-28 md:py-36 px-6" data-testid="section-what-this-is-not">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">Important</p>
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-16">{d.not_heading}</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-16">
          {d.not_items?.map((item, i) => (
            <motion.div key={i} variants={fadeUp} className="flex items-center gap-4 px-6 py-4 rounded-xl border border-white/[0.06] bg-white/[0.02]">
              <div className="w-5 h-5 rounded-full border border-zinc-600 flex items-center justify-center shrink-0">
                <div className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
              </div>
              <p className="font-mc-b text-sm text-zinc-300 text-left">{item}</p>
            </motion.div>
          ))}
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <p className="font-mc-b text-base text-zinc-300 leading-relaxed mb-6">{d.not_closing}</p>
          <p className="font-mc-b text-sm text-zinc-500 leading-relaxed italic">{d.not_emergency}</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ PACKAGES ═══════════════════ */
function Packages({ d, onBook, contactRef }) {
  const pwyw = d.pwyw_enabled;
  const specialOn = d.special_enabled;
  const specialLabel = d.special_label || "Limited Time";

  return (
    <section id="packages" className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-packages">
      <div className="max-w-6xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-20">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Mirror Conversations&trade;</p>
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0]">{d.packages_heading}</h2>
          {pwyw && d.pwyw_text && (
            <p className="font-mc-h text-lg text-zinc-400 italic mt-4">{d.pwyw_text}</p>
          )}
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {d.packages?.map((pkg, i) => {
            const hasSale = specialOn && pkg.sale_price > 0 && pkg.sale_price < pkg.price;
            const displayPrice = hasSale ? pkg.sale_price : pkg.price;

            return (
              <motion.div key={i} variants={fadeUp} className={`relative rounded-2xl p-8 flex flex-col border transition-all duration-300 ${
                pkg.popular
                  ? "bg-[#D4AF37]/[0.06] border-[#D4AF37]/40 shadow-[0_0_30px_rgba(212,175,55,0.1)]"
                  : "bg-white/[0.03] border-white/[0.06] hover:border-[#D4AF37]/30"
              }`} data-testid={`package-card-${i}`}>
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A0A0A] font-mc-b text-[10px] tracking-[0.2em] uppercase px-4 py-1 rounded-full">Most Popular</div>
                )}
                {hasSale && (
                  <div className="absolute -top-3 right-4 bg-[#C0392B] text-white font-mc-b text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-full" data-testid={`sale-badge-${i}`}>{specialLabel}</div>
                )}
                <div className="mb-6">
                  <h3 className="font-mc-h text-xl text-[#F5F5F0] font-light mb-1">{pkg.title}</h3>
                  {pkg.subtitle && <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">{pkg.subtitle}</p>}
                </div>
                {pkg.price > 0 && (
                  <div className="mb-6">
                    {pwyw ? (
                      <>
                        <span className="font-mc-b text-xs text-zinc-500 block mb-1">Starting at</span>
                        {hasSale && <span className="font-mc-h text-xl text-zinc-600 font-light line-through mr-2">${pkg.price}</span>}
                        <span className="font-mc-h text-4xl text-[#F5F5F0] font-light">${displayPrice}</span>
                        <span className="font-mc-b text-sm text-zinc-500 ml-1">+</span>
                      </>
                    ) : hasSale ? (
                      <div className="flex items-baseline gap-3">
                        <span className="font-mc-h text-xl text-zinc-600 font-light line-through">${pkg.price}</span>
                        <span className="font-mc-h text-4xl font-light" style={{ color: "#D4AF37" }}>${displayPrice}</span>
                      </div>
                    ) : (
                      <span className="font-mc-h text-4xl text-[#F5F5F0] font-light">${pkg.price}</span>
                    )}
                  </div>
                )}
                <p className="font-mc-b text-sm text-zinc-400 leading-relaxed flex-1 mb-8">{pkg.desc}</p>
                {pkg.type === "stripe" ? (
                  <Link to="/book" className={`w-full py-4 rounded-full font-mc-b tracking-wide uppercase text-sm transition-all duration-300 text-center block ${
                    pkg.popular
                      ? "bg-[#D4AF37] text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]"
                      : "border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                  }`} data-testid={`package-btn-${i}`}>{pkg.btn_text}</Link>
                ) : (
                  <Link to="/book" className="w-full border border-zinc-700 text-zinc-400 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:border-zinc-500 hover:text-zinc-200 transition-all duration-300 text-center block" data-testid={`package-btn-${i}`}>{pkg.btn_text}</Link>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ SCHOLARSHIP ═══════════════════ */
function Scholarship({ d, onOpen }) {
  if (!d.scholarship_enabled) return null;
  return (
    <section className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-scholarship">
      <div className="max-w-4xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <div className="relative rounded-2xl border border-[#D4AF37]/30 bg-[#0D0D0D] overflow-hidden">
            {/* Warm spotlight effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#D4AF37]/[0.04] rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[200px] bg-[#D4AF37]/[0.06] rounded-full blur-[60px] pointer-events-none" />

            <div className="relative z-10 p-10 md:p-16 text-center">
              <div className="w-16 h-16 mx-auto rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-8">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" className="opacity-80">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>

              <h2 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0] mb-8 leading-tight">{d.scholarship_heading}</h2>

              <div className="font-mc-b text-base text-zinc-300 leading-[1.9] whitespace-pre-line max-w-2xl mx-auto mb-10">{d.scholarship_copy}</div>

              <div className="gold-divider max-w-xs mx-auto mb-10" />

              {d.scholarship_form_enabled && (
                <button onClick={onOpen} className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all duration-300 px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-testid="scholarship-btn">
                  {d.scholarship_btn_text}
                </button>
              )}

              {d.scholarship_availability && (
                <p className="font-mc-b text-xs text-zinc-600 mt-8 italic">{d.scholarship_availability}</p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ SCHOLARSHIP MODAL ═══════════════════ */
function ScholarshipModal({ d, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", conversation_type: "30 Minute Mirror Conversation\u2122", reason: "", clarity_goal: "", preferred_times: "", disclaimer_acknowledged: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = "auto"; }; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.disclaimer_acknowledged) { toast.error("Please acknowledge the disclaimer."); return; }
    setSubmitting(true);
    try {
      const { data } = await http.post("/mirror/public/scholarship", form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#111] border border-[#D4AF37]/30 rounded-2xl w-full max-w-lg relative overflow-hidden flex flex-col max-h-[90vh]" data-testid="scholarship-modal">
        <div className="p-8 border-b border-white/[0.06] flex justify-between items-start">
          <div>
            <h3 className="font-mc-h text-2xl text-[#F5F5F0]">Scholarship Request</h3>
            <p className="font-mc-b text-xs text-zinc-500 mt-1">Mirror Conversations&trade;</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><Plus size={20} className="rotate-45" /></button>
        </div>
        <div className="p-8 overflow-y-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-6">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <p className="font-mc-h text-xl text-[#F5F5F0] font-light leading-relaxed">{d.scholarship_confirmation}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Name *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Email *</label>
                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Phone Number</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Which conversation? *</label>
                <select required value={form.conversation_type} onChange={e => setForm({ ...form, conversation_type: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors [&>option]:bg-[#111]">
                  <option>30 Minute Mirror Conversation&trade;</option>
                  <option>45 Minute Mirror Conversation&trade;</option>
                  <option>60 Minute Mirror Conversation&trade;</option>
                </select>
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Why are you requesting a scholarship seat? *</label>
                <textarea required rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none" />
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">What are you hoping to get clarity on? *</label>
                <textarea required rows={2} value={form.clarity_goal} onChange={e => setForm({ ...form, clarity_goal: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none" />
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Preferred days/times</label>
                <input value={form.preferred_times} onChange={e => setForm({ ...form, preferred_times: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer group pt-2">
                <input type="checkbox" checked={form.disclaimer_acknowledged} onChange={e => setForm({ ...form, disclaimer_acknowledged: e.target.checked })} className="w-4 h-4 accent-[#D4AF37] mt-0.5 shrink-0" />
                <span className="font-mc-b text-xs text-zinc-400 leading-relaxed group-hover:text-zinc-300 transition-colors">I understand Mirror Conversations&trade; is not therapy, counseling, mental health treatment, or crisis support.</span>
              </label>
              <button type="submit" disabled={submitting || !form.disclaimer_acknowledged} className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-40 mt-2" data-testid="scholarship-submit">
                {submitting ? "Submitting..." : "Submit Scholarship Request"}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════ TESTIMONIALS ═══════════════════ */
function Testimonials({ d }) {
  if (!d.testimonials?.length) return null;
  return (
    <section className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-testimonials">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-20">
          <h2 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0]">{d.testimonials_heading}</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
          {d.testimonials.map((t, i) => (
            <motion.div key={i} variants={fadeUp} className="rounded-2xl p-8 bg-white/[0.03] border border-white/[0.06] flex flex-col">
              <div className="text-[#D4AF37] font-mc-h text-5xl leading-none mb-4 opacity-40">&ldquo;</div>
              <p className="font-mc-h text-lg text-[#F5F5F0] font-light italic leading-relaxed flex-1">{t.quote}</p>
              {t.name && <p className="font-mc-b text-xs tracking-[0.2em] uppercase text-zinc-500 mt-6">&mdash; {t.name}</p>}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ FAQ ═══════════════════ */
function Faq({ d }) {
  const [open, setOpen] = useState(null);
  if (!d.faq?.length) return null;
  return (
    <section id="faq" className="bg-[#0A0A0A] py-28 md:py-36 px-6" data-testid="section-faq">
      <div className="max-w-3xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0]">{d.faq_heading}</h2>
        </motion.div>
        <div className="space-y-3">
          {d.faq.map((item, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-4 rounded-xl px-6 py-5 bg-white/[0.03] border border-white/[0.06] hover:border-[#D4AF37]/30 transition-colors text-left" data-testid={`faq-q-${i}`}>
                <span className="font-mc-h text-lg text-[#F5F5F0] font-light">{item.q}</span>
                <ChevronDown size={18} className={`text-[#D4AF37] shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                    <div className="px-6 py-4 font-mc-b text-sm text-zinc-400 leading-relaxed">{item.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ FINAL CTA ═══════════════════ */
function FinalCTA({ d }) {
  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-[#0A0A0A]" data-testid="section-final-cta">
      <div className="absolute inset-0 z-0">
        <img src={resolveImage(d.final_image)} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/40" />
      </div>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <h2 className="font-mc-h text-4xl md:text-6xl lg:text-7xl font-light text-[#F5F5F0] mb-8">{d.final_heading}</h2>
        <p className="font-mc-h text-xl md:text-2xl text-zinc-400 font-light italic whitespace-pre-line leading-relaxed mb-12">{d.final_copy}</p>
        <Link to="/book" className="inline-block bg-[#D4AF37] text-[#0A0A0A] px-12 py-5 rounded-full font-mc-b tracking-wide uppercase text-sm gold-glow hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300" data-testid="final-cta-btn">{d.final_btn}</Link>
      </motion.div>
    </section>
  );
}

/* ═══════════════════ FOOTER NOTE + CONTACT ═══════════════════ */
function FooterNote({ d, contactRef }) {
  return (
    <section ref={contactRef} className="bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="section-footer-note">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/[0.06]">
        <div className="p-12 md:p-20">
          <h3 className="font-mc-h text-2xl text-[#F5F5F0] mb-8">Have a question?</h3>
          <ContactForm />
        </div>
        <div className="p-12 md:p-20">
          <h3 className="font-mc-h text-2xl text-[#F5F5F0] mb-6">Stay connected</h3>
          <p className="font-mc-b text-sm text-zinc-500 mb-8">Get updates on events, workshops, and new offerings.</p>
          <form className="flex gap-0">
            <input type="email" placeholder="Enter your email" className="bg-transparent border border-white/10 border-r-0 text-[#F5F5F0] px-5 py-4 flex-1 focus:border-[#D4AF37] focus:outline-none font-mc-b text-sm rounded-l-full" />
            <button type="button" className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-4 font-mc-b text-xs tracking-[0.15em] uppercase rounded-r-full hover:bg-[#E5C86C] transition-colors">Subscribe</button>
          </form>
        </div>
      </div>
      <div className="border-t border-white/[0.06] py-8 px-6">
        <p className="font-mc-b text-xs text-zinc-600 text-center max-w-3xl mx-auto leading-relaxed">{d.footer_disclaimer}</p>
      </div>
    </section>
  );
}

/* ═══════════════════ REFLECTION LIBRARY CTA ═══════════════════ */
function ReflectionLibraryCTA({ d }) {
  if (d.rl_enabled === false) return null;

  const features = d.rl_features || [];
  const previews = d.rl_previews || [];
  const featureIcons = [
    <svg key="rl-icon-0" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>,
    <svg key="rl-icon-1" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
    <svg key="rl-icon-2" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    <svg key="rl-icon-3" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  ];

  return (
    <section className="bg-[#F8F5F0] py-24 md:py-32 px-6 overflow-hidden" data-testid="section-reflection-library">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Reflection Library&trade;</p>
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#1A1918] leading-tight mb-5">{d.rl_headline}</h2>
          <p className="font-mc-b text-base text-[#5C5A56] leading-relaxed max-w-3xl mx-auto">{d.rl_subheadline}</p>
        </motion.div>

        {/* Body Copy */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mx-auto text-center mb-20">
          <div className="font-mc-b text-base text-[#3A3835] leading-[1.9] whitespace-pre-line">{d.rl_body}</div>
        </motion.div>

        {/* Feature Cards */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {features.map((f, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-7 border border-[#E8E4DD] hover:border-[#D4AF37]/40 transition-all duration-300 hover:shadow-lg text-center" data-testid={`rl-feature-${i}`}>
              <div className="text-[#D4AF37] mb-5 flex justify-center">{featureIcons[i] || featureIcons[0]}</div>
              <h3 className="font-mc-b text-sm font-medium text-[#1A1918] mb-2 tracking-wide">{f.title}</h3>
              <p className="font-mc-b text-xs text-[#8A857D] leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Featured Worksheets */}
        {previews.length > 0 && (
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-16">
            <h3 className="font-mc-h text-2xl text-[#1A1918] text-center mb-8 font-light">Featured Worksheets</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {previews.map((title, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="bg-white border border-[#E8E4DD] rounded-xl px-5 py-3 hover:border-[#D4AF37]/40 hover:shadow-md transition-all duration-200 cursor-default" data-testid={`rl-preview-${i}`}>
                  <p className="font-mc-b text-sm text-[#1A1918]">{title}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Divider */}
        <div className="h-px max-w-sm mx-auto mb-12" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)" }} />

        {/* CTA */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center">
          <Link to={d.rl_btn_link || "/reflection-library"} className="inline-block bg-[#1A1918] text-[#F5F5F0] px-10 py-4 rounded-full font-mc-b text-sm tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all duration-300 shadow-lg hover:shadow-xl" data-testid="rl-cta-link">
            {d.rl_btn_text}
          </Link>
          {d.rl_secondary_text && (
            <p className="font-mc-h text-sm text-[#8A857D] font-light italic mt-6 whitespace-pre-line leading-relaxed">{d.rl_secondary_text}</p>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ MEDIA KIT ═══════════════════ */
function MediaKit({ d, contactRef }) {
  if (d.mk_enabled === false) return null;

  const features = d.mk_features || [];
  const featureIcons = [
    <svg key="mk-icon-0" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
    <svg key="mk-icon-1" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    <svg key="mk-icon-2" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>,
  ];

  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6 relative overflow-hidden" data-testid="section-media-kit">
      {d.mk_bg_image && (
        <div className="absolute inset-0 z-0">
          <img src={d.mk_bg_image.startsWith("http") ? d.mk_bg_image : `${process.env.REACT_APP_BACKEND_URL}${d.mk_bg_image}`} alt="" className="w-full h-full object-cover opacity-10" />
        </div>
      )}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 40% at 50% 30%, rgba(212,175,55,0.04) 0%, transparent 100%)" }} />

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Media Kit 2026</p>
          <h2 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-5">{d.mk_headline}</h2>
          <p className="font-mc-b text-base text-zinc-400 leading-relaxed max-w-3xl mx-auto">{d.mk_subheadline}</p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mx-auto text-center mb-16">
          <div className="font-mc-b text-sm text-zinc-300 leading-[1.9] whitespace-pre-line">{d.mk_body}</div>
        </motion.div>

        {/* Feature cards */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-5 mb-16">
          {features.map((f, i) => (
            <motion.div key={i} variants={fadeUp} className="rounded-2xl p-7 bg-white/[0.03] border border-white/[0.06] hover:border-[#D4AF37]/30 transition-all text-center" data-testid={`mk-feature-${i}`}>
              <div className="text-[#D4AF37] mb-4 flex justify-center">{featureIcons[i] || featureIcons[0]}</div>
              <h3 className="font-mc-b text-sm font-medium text-[#F5F5F0] mb-2 tracking-wide">{f.title}</h3>
              <p className="font-mc-b text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Buttons */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {d.mk_pdf_url ? (
              <a href={d.mk_pdf_url.startsWith("http") ? d.mk_pdf_url : `${process.env.REACT_APP_BACKEND_URL}${d.mk_pdf_url}`} target="_blank" rel="noreferrer"
                className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-mc-b text-sm tracking-wide uppercase hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all inline-flex items-center justify-center gap-2" data-testid="mk-download-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
                {d.mk_btn_text}
              </a>
            ) : (
              <span className="border border-zinc-700 text-zinc-600 px-10 py-4 rounded-full font-mc-b text-sm tracking-wide uppercase cursor-not-allowed inline-block">Media Kit Coming Soon</span>
            )}
            <button onClick={() => contactRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="border border-[#D4AF37] text-[#D4AF37] px-10 py-4 rounded-full font-mc-b text-sm tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all" data-testid="mk-request-btn">
              {d.mk_btn2_text}
            </button>
          </div>
          {d.mk_footnote && <p className="font-mc-b text-xs text-zinc-600 italic">{d.mk_footnote}</p>}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════ BOOKING MODAL ═══════════════════ */
function BookingModal({ pkg, minPrice, pwyw, pwywText, suggestedPrice, onClose }) {
  const [step, setStep] = useState(1);
  const effectivePrice = pkg._effectivePrice || pkg.price;
  const fixedPrice = effectivePrice > 0 ? effectivePrice : minPrice;
  const [form, setForm] = useState({ name: "", email: "", amount: String(fixedPrice) });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = "auto"; }; }, []);

  const handleCheckout = async () => {
    const amt = parseFloat(form.amount);
    const floor = pwyw ? minPrice : fixedPrice;
    if (isNaN(amt) || amt < floor) { toast.error(`Minimum amount is $${floor}`); return; }
    setLoading(true);
    try {
      const { data } = await http.post("/mirror/public/checkout", { package_id: pkg.id, amount: amt, name: form.name, email: form.email });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Checkout failed. " + (err.response?.data?.detail || ""));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#111] border border-[#D4AF37]/30 rounded-2xl w-full max-w-lg relative overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-8 border-b border-white/[0.06] flex justify-between items-start">
          <div>
            <h3 className="font-mc-h text-2xl text-[#F5F5F0] mb-1">{pkg.title}</h3>
            <p className="font-mc-b text-xs tracking-[0.2em] text-[#D4AF37] uppercase">{pkg.subtitle}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><Plus size={20} className="rotate-45" /></button>
        </div>
        <div className="p-8 overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-6">
              {pwyw ? (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-5">
                  <p className="font-mc-b text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-3">Honor System Payment</p>
                  <p className="font-mc-b text-sm text-zinc-400 leading-relaxed">{pwywText || "Pay what you can. Take what you need."} Minimum ${minPrice}.</p>
                </div>
              ) : (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-5 text-center">
                  <p className="font-mc-b text-xs tracking-[0.2em] text-[#D4AF37] uppercase mb-2">Session Price</p>
                  <p className="font-mc-h text-4xl text-[#F5F5F0] font-light">${fixedPrice}</p>
                </div>
              )}
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Full Name</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">Email</label>
                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              {pwyw ? (
                <div>
                  <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-[#D4AF37] mb-2 block">Your Amount (Min ${minPrice})</label>
                  <div className="relative">
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xl text-zinc-500">$</span>
                    <input required type="number" min={minPrice} step="1" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                      className="w-full bg-transparent border-b-2 border-white/10 text-[#F5F5F0] font-mc-h text-3xl pl-7 py-3 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/10" placeholder={String(suggestedPrice || fixedPrice)} />
                  </div>
                </div>
              ) : null}
              <button type="button" onClick={() => setStep(2)} disabled={!form.name || !form.email || (pwyw && (!form.amount || parseFloat(form.amount) < minPrice))}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-40 mt-4">
                {pwyw ? "Continue to Payment" : `Pay $${fixedPrice} — Continue`}
              </button>
            </div>
          ) : (
            <div className="text-center py-4 space-y-6">
              <h4 className="font-mc-h text-2xl text-[#F5F5F0]">Review & Pay</h4>
              <p className="font-mc-b text-sm text-zinc-500">Booking {pkg.title} for ${parseFloat(form.amount).toFixed(2)}</p>
              <button onClick={handleCheckout} disabled={loading} className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-mc-b tracking-wide uppercase text-sm flex items-center justify-center gap-3 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all">
                {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <CreditCard size={16} />}
                Pay ${parseFloat(form.amount).toFixed(2)} via Stripe
              </button>
              <div className="flex items-center gap-4 text-zinc-600 font-mc-b text-xs tracking-widest uppercase"><div className="flex-1 h-px bg-white/5" />OR<div className="flex-1 h-px bg-white/5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <a href="https://cash.app/$MickeyTrivett" target="_blank" rel="noreferrer" className="border border-white/10 text-zinc-400 py-3 rounded-full font-mc-b text-xs tracking-widest uppercase hover:border-[#D4AF37]/30 transition-colors text-center">CashApp</a>
                <a href="https://venmo.com/u/MickeyTrivett" target="_blank" rel="noreferrer" className="border border-white/10 text-zinc-400 py-3 rounded-full font-mc-b text-xs tracking-widest uppercase hover:border-[#D4AF37]/30 transition-colors text-center">Venmo</a>
              </div>
              <p className="font-mc-b text-xs text-zinc-600 pt-4 leading-relaxed">Include your email in the payment note. We&apos;ll reach out to schedule.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════ CONTACT FORM ═══════════════════ */
function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interested_in: "", message: "", is_scholarship: false, scholarship_reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await http.post("/mirror/public/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", interested_in: "", message: "", is_scholarship: false, scholarship_reason: "" });
    } catch { toast.error("Failed to send."); } finally { setSubmitting(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      <input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-600 text-sm" />
      <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-600 text-sm" />
      <select value={form.interested_in} onChange={e => setForm({ ...form, interested_in: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors [&>option]:bg-[#111] text-sm">
        <option value="" className="text-zinc-600">What are you interested in?</option>
        <option>30 Minute Conversation</option>
        <option>45 Minute Conversation</option>
        <option>60 Minute Conversation</option>
        <option>Speaking Request</option>
        <option>Scholarship Inquiry</option>
        <option>General Question</option>
      </select>
      <textarea required placeholder="Your message" rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-600 resize-none text-sm" />
      <label className="flex items-center gap-3 cursor-pointer group">
        <input type="checkbox" checked={form.is_scholarship} onChange={e => setForm({ ...form, is_scholarship: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
        <span className="font-mc-b text-xs text-zinc-500 group-hover:text-[#D4AF37] transition-colors">I am requesting a scholarship</span>
      </label>
      <AnimatePresence>
        {form.is_scholarship && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <textarea placeholder="Briefly explain your situation..." rows={2} value={form.scholarship_reason} onChange={e => setForm({ ...form, scholarship_reason: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 text-[#F5F5F0] font-mc-b px-4 py-3 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-600 resize-none text-sm rounded-lg" />
          </motion.div>
        )}
      </AnimatePresence>
      <button disabled={submitting} className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-mc-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-40">
        {submitting ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}
